import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import api from "../../api/api";
import { useTranslation } from "react-i18next";

const URLBiodata = ({ extractedBiodata }) => {
  const { t } = useTranslation();
  const [error, setError] = useState();
  const [blockUploading, setBlockUploading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [result, setResult] = useState(null);
  const [linkAddress, setLinkAddress] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const handleLookup = async () => {
    if (!email.trim()) {
      setError(t('Please enter your email address'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('Please enter a valid email address '));
      return;
    }
    if (!password.trim()) {
      setError(t('Please enter your password of the link'));
      return;
    }
    if (!linkAddress.trim()) {
      setError(t('Please enter a valid link address'));
      return;
    }
    if(blockUploading) {
      return; 
    }
    setBlockUploading(true);
    setResult("processing");
    setError("");

    try {
      const formData = new FormData();
      formData.append('url', linkAddress);
      if (email.trim()) {
        formData.append('email', email);
      }
      if (password.trim()) {
        formData.append('password', password);
      }

      const res = await api.post('/biodata/lookup/', formData);
      setTaskId(res.data.task_id);
      setResult("submitted");
    } catch (error) {
      setError(t(error.response?.data?.error || t('An error occurred while processing the link')));
      setResult(null);
    } finally {
      setBlockUploading(false);
    }
  };

  useEffect(() => {
    if (!taskId) return;

    const interval = setInterval(async () => {
      const res = await api.get(`/task-status/${taskId}/`);
      if (res.data.status === 'SUCCESS') {
        let getresult = JSON.parse(res.data.result)
        if(!getresult){
          setError(t('Login failed - unable to authenticate'));
          setResult(null);
          clearInterval(interval);
          return;
        }
        extractedBiodata(JSON.parse(res.data.result));
        setResult("success");
        clearInterval(interval);
      }else if (res.data.status === 'FAILURE') {
        setError(t('An error occurred while processing the link'));
        setResult(null);
        clearInterval(interval);
      } else if (res.data.status === 'PENDING') {
        setResult("pending");
      }else {
        setResult("processing");
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [taskId]);

  const renderStatus = () => {
    if (!result) return null;

    const statusConfig = {
      processing: {
        icon: 'fas fa-cog fa-spin',
        color: 'primary',
        bgColor: 'rgba(13, 110, 253, 0.1)',
        borderColor: '#0d6efd',
        message: t('Processing link...')
      },
      submitted: {
        icon: 'fas fa-paper-plane',
        color: 'info',
        bgColor: 'rgba(13, 202, 240, 0.1)',
        borderColor: '#0dcaf0',
        message: t('Link submitted successfully, we are now processing the data...')
      },
      pending: {
        icon: 'fas fa-clock',
        color: 'warning',
        bgColor: 'rgba(255, 193, 7, 0.1)',
        borderColor: '#ffc107',
        message: t('Please wait, we are processing your data...')
      },
      success: {
        icon: 'fas fa-check-circle',
        color: 'success',
        bgColor: 'rgba(25, 135, 84, 0.1)',
        borderColor: '#198754',
        message: t('Data processed successfully!')
      }
    };

    const config = statusConfig[result];
    if (!config) return null;

    return (
      <div 
        className="mt-3 p-3 rounded-3 border-start border-3"
        style={{ 
          backgroundColor: config.bgColor,
          borderLeftColor: config.borderColor,
          transition: 'all 0.3s ease'
        }}
      >
        <div className="d-flex align-items-center">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ 
              width: '40px', 
              height: '40px', 
              backgroundColor: config.borderColor + '20' 
            }}
          >
            <i className={`${config.icon} text-${config.color}`}></i>
          </div>
          <div className="flex-grow-1">
            <p className={`text-${config.color} fw-semibold mb-0`}>
              {config.message}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="modern-url-container" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Modern Credentials Section */}
      <div className="mb-4">
        <div className="p-4 rounded-4 border" style={{ backgroundColor: 'rgba(248, 249, 250, 0.8)', borderColor: '#e9ecef' }}>
          <div className="d-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: '48px', 
                height: '48px', 
                backgroundColor: 'rgba(13, 110, 253, 0.1)' 
              }}
            >
              <i className="fas fa-user-shield text-primary" style={{ fontSize: '1.2rem' }}></i>
            </div>
            <div>
              <h6 className="mb-1 fw-bold text-dark">{t("Login Credentials")}</h6>
              <p className="text-muted small mb-0">{t("Secure authentication for data extraction")}</p>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <div className="position-relative">
                <input
                  type="email"
                  className="form-control ps-5"
                  placeholder={t("Email Address")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ 
                    borderRadius: '12px',
                    border: '2px solid #e9ecef',
                    padding: '12px 16px 12px 45px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0d6efd';
                    e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <i className="fas fa-envelope position-absolute" 
                   style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}></i>
              </div>
            </div>
            <div className="col-md-6">
              <div className="position-relative">
                <input
                  type="password"
                  className="form-control ps-5"
                  placeholder={t("Password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={{ 
                    borderRadius: '12px',
                    border: '2px solid #e9ecef',
                    padding: '12px 16px 12px 45px',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0d6efd';
                    e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <i className="fas fa-lock position-absolute" 
                   style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}></i>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-3 p-3 rounded-3" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)', border: '1px solid rgba(25, 135, 84, 0.2)' }}>
            <div className="d-flex align-items-start">
              <i className="fas fa-shield-alt text-success me-2 mt-1" style={{ fontSize: '14px' }}></i>
              <small className="text-success">
                <strong>{t("100% Secure:")}</strong> {t("Your credentials are encrypted and used only for temporary authentication. We never store or access your personal information.")}
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Modern URL Input Section */}
      <div className="mb-4">
        <div className="p-4 rounded-4 border" style={{ backgroundColor: 'rgba(248, 249, 250, 0.8)', borderColor: '#e9ecef' }}>
          <div className="d-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: '48px', 
                height: '48px', 
                backgroundColor: 'rgba(13, 110, 253, 0.1)' 
              }}
            >
              <i className="fas fa-link text-primary" style={{ fontSize: '1.2rem' }}></i>
            </div>
            <div>
              <h6 className="mb-1 fw-bold text-dark">{t("Link Address")}</h6>
              <p className="text-muted small mb-0">{t("Enter the URL to extract biodata from")}</p>
            </div>
          </div>

          <div className="d-flex gap-3 align-items-center flex-wrap">
            <div className="flex-grow-1 position-relative" >
              <input
                type="url"
                id="linkaddress"
                name="linkaddress"
                className="form-control ps-5"
                placeholder={t("https://example.com/biodata-link")}
                value={linkAddress}
                onChange={(e) => setLinkAddress(e.target.value)}
                style={{ 
                  borderRadius: '12px',
                  border: '2px solid #e9ecef',
                  padding: '12px 16px 12px 45px',
                  fontSize: '14px',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#0d6efd';
                  e.target.style.boxShadow = '0 0 0 0.2rem rgba(13, 110, 253, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e9ecef';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <i className="fas fa-globe position-absolute" 
                 style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}></i>
            </div>

            <button 
              type="button" 
              className="btn btn-primary px-4 py-2"
              onClick={handleLookup}
              disabled={blockUploading}
             
              onMouseEnter={(e) => {
                if (!blockUploading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 8px rgba(13, 110, 253, 0.2)';
              }}
            >
              {blockUploading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  {t("Processing...")}
                </>
              ) : (
                <>
                  <i className="fas fa-search me-2"></i>
                  {t("Extract Data")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Status Messages */}
      {renderStatus()}

      {/* Error Message */}
      {error && (
        <div className="mt-3 p-3 rounded-3 border-start border-danger border-3" 
             style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
          <div className="d-flex align-items-center">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ 
                width: '40px', 
                height: '40px', 
                backgroundColor: 'rgba(220, 53, 69, 0.2)' 
              }}
            >
              <i className="fas fa-exclamation-triangle text-danger"></i>
            </div>
            <div className="flex-grow-1">
              <p className="text-danger fw-semibold mb-0">{error}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

URLBiodata.propTypes = {
  extractedBiodata: PropTypes.func
};

export default URLBiodata;